/**
 * Sanctums — the gate between biome bands. Issue #1 phase 4.
 *
 * This is Noita's Holy Mountain, and the load-bearing part of it is the
 * material, not the furniture. In a game where nearly everything is a cell you
 * can burn, dissolve or blow up, a checkpoint made of rock is a checkpoint made
 * of suggestion. The shell here is `ADAMANT_SEAL`, which is `indestructible`,
 * which `abilities.ts` `blocksTranslocation` already refuses to Blink or Phase
 * Drift through — so the one structure a run guarantees is also the one
 * structure a run cannot casually remove. docs/01 §3.4: "if you can melt it, it
 * was never a gate."
 *
 * WHAT A SANCTUM IS NOT is as important as what it is. It is **not** a rest
 * node (#6). A sanctum is one per band boundary, guaranteed, on the spine,
 * found by descending. A rest node is optional, in-band, out on the flanks,
 * found by going sideways. Collapsing the two would leave horizontal
 * exploration with nothing to offer, which is the whole reason #6 records them
 * as separate things.
 *
 * DETERMINISM. Everything here is a pure function of `(seed, band)` — no
 * generation order, no clock, no neighbour queries. `worldgen.ts` asks which
 * sanctums overlap a chunk and stamps its slice of them, so a chunk regenerates
 * byte-identically after eviction and two chunks that share a shaft agree on
 * where its rungs are without talking (they are keyed on absolute world y).
 *
 * ZERO ABILITIES. Every drop into, through and out of a sanctum is inside the
 * 54-cell jump height in *both* directions, and every landing is a one-way
 * floor rather than a shelf, so the chamber is a descent, a climb back up, and
 * never a soft-lock. That is the same contract the descent shaft signs in
 * `worldgen.ts`, and `tests/sanctum.test.ts` measures it the same way.
 */

import { hash2 } from '../core/rng';

/** Interior width. Wide enough that three services are a walk, not a pile. */
export const SANCTUM_IN_W = 180;
/**
 * Interior height. 48 is chosen against the movement package rather than for
 * looks: it is over twice the 22-cell body, so the room reads as a hall, and it
 * is under the 54-cell jump height, so a player who dropped in through the
 * ceiling can get back out of it without an ability.
 */
export const SANCTUM_IN_H = 48;
/** Shell thickness. Six cells is thicker than any dig radius in the game. */
export const SANCTUM_SHELL = 6;
/** How far the approach and exit shafts run beyond the shell. */
export const SANCTUM_APPROACH = 120;
/** Rung pitch in both shafts. Under `jumpHeight`, so a shaft is also a ladder. */
export const SANCTUM_STEP = 26;
/** Width of the ceiling and floor apertures. */
export const SANCTUM_GAP_W = 26;
/** How close the player stands to use a service. Matches the altar's reach. */
export const SANCTUM_RANGE = 22;
/** Fraction of max HP the pool restores. Once. */
export const POOL_HEAL = 0.5;

/**
 * How far off the spine a sanctum may sit.
 *
 * Not zero. A checkpoint pinned to x = 0 on every seed is a landmark you stop
 * navigating to and start assuming, and the descent is not a straight line
 * anyway — the corridor router puts the spine wherever the herringbone lattice
 * does. ±240 is inside one screen of the axis and well clear of the rest-node
 * band out at 420.
 */
const SANCTUM_DRIFT = 240;

export interface Sanctum {
  /** The band this sanctum admits you *into*. Boundary 1 opens band 1. */
  band: number;
  /** World y of the band line the chamber straddles. */
  boundaryY: number;
  /** Chamber centre. */
  x: number;
  /** World y of the top cell of the floor slab — the surface you stand on. */
  floorY: number;
  /** World y of the lowest ceiling cell. */
  ceilY: number;
  /** Interior span, inclusive. */
  x0: number;
  x1: number;
  /** The hole in the ceiling you arrive through. */
  entryX0: number;
  entryX1: number;
  /** The hole in the floor you leave through. */
  exitX0: number;
  exitX1: number;
  /** The healing pool's rim. */
  pool: { x: number; y: number; halfW: number };
  /** Plinth top for the weapon altar (`weaponAltar.ts`). */
  altar: { x: number; y: number };
  /** Pedestal top for the ability cache (`abilityCache.ts`). */
  cache: { x: number; y: number };
}

/**
 * The sanctum at a band boundary. Pure in `(seed, band)`.
 *
 * `bandHeight` is a parameter rather than an import so a caller with a flat
 * test world gets a coherent answer instead of a sanctum in the wrong place.
 */
export function sanctumAt(seed: number, band: number, bandHeight: number): Sanctum {
  const boundaryY = band * bandHeight;
  const h = hash2(band, 0x5a1c, seed ^ 0x50a17c);
  const x = (h % (SANCTUM_DRIFT * 2 + 1)) - SANCTUM_DRIFT;

  // The chamber straddles the line: its ceiling is above the boundary and its
  // floor below it, so crossing a band and entering a sanctum are one act.
  const floorY = boundaryY + 24;
  const half = SANCTUM_IN_W >> 1;
  const x0 = x - half;
  const x1 = x + half - 1;
  const ceilY = floorY - SANCTUM_IN_H - 1;

  // Entry left, exit right, so the room is walked across rather than dropped
  // through — which is what puts the three services on the way.
  const entryX0 = x - 74;
  const exitX0 = x + 49;

  return {
    band,
    boundaryY,
    x,
    floorY,
    ceilY,
    x0,
    x1,
    entryX0,
    entryX1: entryX0 + SANCTUM_GAP_W - 1,
    exitX0,
    exitX1: exitX0 + SANCTUM_GAP_W - 1,
    // Left to right in the order you walk them: what opened, what heals, what
    // you fight the next band with.
    pool: { x: x - 14, y: floorY, halfW: 12 },
    altar: { x: x + 12, y: floorY - 3 },
    cache: { x: x - 38, y: floorY - 3 },
  };
}

/** Vertical extent including both shafts, as `[top, bottom]` world y. */
export function sanctumSpanY(s: Sanctum): [number, number] {
  return [
    s.ceilY - SANCTUM_SHELL + 1 - SANCTUM_APPROACH,
    s.floorY + SANCTUM_SHELL + SANCTUM_APPROACH,
  ];
}

/** Every sanctum whose full extent intersects `[y0, y1]`. */
export function sanctumsOverlapping(
  seed: number,
  bands: number,
  bandHeight: number,
  y0: number,
  y1: number,
): Sanctum[] {
  const out: Sanctum[] = [];
  // Only boundaries within one span of the range can matter, so this is two or
  // three candidates rather than a scan of the roster.
  const reach = SANCTUM_IN_H + SANCTUM_SHELL * 2 + SANCTUM_APPROACH + 48;
  let lo = Math.floor((y0 - reach) / bandHeight);
  let hi = Math.floor((y1 + reach) / bandHeight);
  if (lo < 1) lo = 1;
  if (hi > bands - 1) hi = bands - 1;
  for (let b = lo; b <= hi; b++) {
    const s = sanctumAt(seed, b, bandHeight);
    const [top, bottom] = sanctumSpanY(s);
    if (bottom >= y0 && top <= y1) out.push(s);
  }
  return out;
}

/** All of them, top to bottom. */
export function allSanctums(seed: number, bands: number, bandHeight: number): Sanctum[] {
  const out: Sanctum[] = [];
  for (let b = 1; b < bands; b++) out.push(sanctumAt(seed, b, bandHeight));
  return out;
}

/**
 * The sanctum whose chamber a point is inside, or null.
 *
 * The chamber and not the shafts: arriving is what the integrator reacts to
 * (raise the altar, place the cache, offer the pool), and a player forty cells
 * up the approach has not arrived. The test is a rect rather than a radius
 * because the room is one, and a radius would fire early at the corners.
 */
export function sanctumContaining(
  list: readonly Sanctum[],
  px: number,
  py: number,
): Sanctum | null {
  for (const s of list) {
    if (px < s.x0 || px > s.x1) continue;
    if (py < s.floorY - SANCTUM_IN_H || py > s.floorY) continue;
    return s;
  }
  return null;
}

// --------------------------------------------------------------- the visit
//
// Run-scoped state, kept out of the geometry so the geometry stays pure. The
// integrator owns one of these per sanctum the player has entered.

export interface SanctumVisit {
  sanctum: Sanctum;
  /** The pool is a single draught, not a fountain. */
  poolUsed: boolean;
  atPool: boolean;
}

export function newVisit(sanctum: Sanctum): SanctumVisit {
  return { sanctum, poolUsed: false, atPool: false };
}

/** Refresh what the player is standing at. `SANCTUM_RANGE` is the altar's. */
export function updateVisit(v: SanctumVisit, px: number, py: number): void {
  const p = v.sanctum.pool;
  const dx = p.x - px;
  const dy = p.y - py;
  v.atPool = !v.poolUsed && dx * dx + dy * dy < SANCTUM_RANGE * SANCTUM_RANGE;
}

/**
 * Drink. Returns the fraction of max HP to restore, or null when out of range
 * or already drunk.
 *
 * A fraction rather than a number because the integrator owns HP and the
 * archetype owns the multiplier — handing back an absolute would make the pool
 * quietly worse for the tankiest build, which is backwards.
 */
export function drinkPool(v: SanctumVisit): number | null {
  if (!v.atPool || v.poolUsed) return null;
  v.poolUsed = true;
  v.atPool = false;
  return POOL_HEAL;
}
